/**
 * The avatar's corner badge: the profile pin and the vibe icon, together.
 *
 * Deliberately NOT drawn inside the illustrated SVG. That SVG is rendered
 * inside a circular crop (`ui/avatar.tsx`), so anything in it is confined to
 * the face's circle — a badge there either sits over the person or gets
 * sliced by the mask. Drawn as an overlay instead, the badge can straddle the
 * circle's edge exactly the way the presence dot does: mostly outside,
 * cutting a notch into the avatar, big enough to actually read.
 *
 * Zero imports on purpose, so the root `node --test` suites can reach it.
 */

export const PROFILE_PIN_GLYPH: Record<string, string> = {
  pride: "🏳️‍🌈",
  trans: "🏳️‍⚧️",
  neurodivergent: "♾️",
  hidden_disability: "🌻",
  accessibility: "♿",
  deaf_hoh: "🦻",
  service_animal: "🐕‍🦺",
  mask_friendly: "😷",
  alcohol_free: "🚫🍸",
  alcohol_friendly: "🍸",
  cannabis_friendly: "🌿",
  no_smoking: "🚭",
  quiet_places: "🔉",
  low_sensory: "🎧",
  small_groups: "👥",
  one_to_one: "👤",
  text_first: "💬",
  new_to_area: "📍",
};

export const VIBE_GLYPH: Record<string, string> = {
  coffee: "☕",
  book: "📖",
  music: "🎵",
  walk: "🚶",
  game: "🎮",
  art: "🎨",
  sport: "⚽",
  food: "🍽️",
  volunteer: "🤝",
  study: "✏️",
};

/**
 * How much horizontal room a pin needs, in glyph-widths. `alcohol_free` is
 * the one pin drawn from two emoji rather than one.
 */
const PIN_GLYPH_UNITS: Record<string, number> = { alcohol_free: 2 };

/** Badge diameter as a fraction of the avatar's size. */
export const BADGE_SIZE_RATIO = 0.4;

/** Ring thickness as a fraction of the badge diameter. */
export const BADGE_RING_RATIO = 0.09;

/**
 * How far the badge hangs past the avatar's bounding box, as a fraction of
 * the avatar's size — negative, i.e. outside it.
 *
 * Derived rather than eyeballed: the badge's centre should land exactly ON
 * the avatar's rim, so the rim bisects it. The 45° point of a circle of
 * radius s/2 is (1+1/√2)·s/2 along each axis, which leaves this much between
 * the badge's far edge and the box.
 */
export const BADGE_EDGE_RATIO = (1 - Math.SQRT1_2) / 2 - BADGE_SIZE_RATIO / 2;

/**
 * An emoji's advance width relative to its font size. Emoji are wider than
 * they are tall, which is why sizing them by the badge's diameter alone
 * overflowed the circle on small avatars.
 */
const GLYPH_ADVANCE = 1.2;

/** How much of the badge's inner width the glyphs may occupy. */
const GLYPH_FILL = 0.94;

/** Largest a single glyph may be, as a fraction of the inner circle. */
const GLYPH_MAX = 0.66;

/**
 * Below this badge diameter two symbols cannot both be read, so the badge
 * shows one — the pin, since an access need outranks a hobby. The full pair
 * is still there on any screen that draws the avatar larger.
 */
const COMPACT_BADGE_PX = 30;

export type AvatarBadge = {
  /** The glyphs to draw, pin first. */
  text: string;
  /** Font size for the badge, given its diameter. */
  fontSize: number;
};

type BadgeSource = {
  profilePin?: string | null;
  vibeIcon?: string | null;
};

/**
 * What the corner badge should draw for this config, or null when it draws
 * nothing — which is when the screens fall back to the presence dot.
 *
 * The pin comes first: an access need reads before a hobby.
 */
export function avatarBadge(
  config: BadgeSource | null | undefined,
  diameter: number,
): AvatarBadge | null {
  if (!config) return null;

  const pin = config.profilePin && config.profilePin !== "none" ? config.profilePin : null;
  const vibe = config.vibeIcon && config.vibeIcon !== "none" ? config.vibeIcon : null;

  const pinGlyph = pin ? (PROFILE_PIN_GLYPH[pin] ?? "") : "";
  const vibeGlyph = vibe ? (VIBE_GLYPH[vibe] ?? "") : "";
  if (!pinGlyph && !vibeGlyph) return null;

  const pinUnits = pin ? (PIN_GLYPH_UNITS[pin] ?? 1) : 0;

  // On a small avatar, two symbols crammed together read as neither.
  const compact = diameter < COMPACT_BADGE_PX;
  const showBoth = Boolean(pinGlyph && vibeGlyph) && !compact;

  const text = showBoth ? `${pinGlyph}${vibeGlyph}` : pinGlyph || vibeGlyph;
  const units = showBoth ? pinUnits + 1 : pinGlyph ? pinUnits : 1;

  // Sized against the space INSIDE the ring, not the outer circle, and
  // against the glyphs' real width rather than their height.
  const inner = diameter * (1 - 2 * BADGE_RING_RATIO);
  const fontSize = Math.min(
    (inner * GLYPH_FILL) / (units * GLYPH_ADVANCE),
    inner * GLYPH_MAX,
  );

  return { text, fontSize };
}

/** Whether this config draws a corner badge at all. */
export function hasAvatarBadge(config: BadgeSource | null | undefined): boolean {
  return avatarBadge(config, 1) !== null;
}
